/* ------------------------------------------------------------------------------
 *
 *  # Firebase Cloud Messaging - Minimal Integration
 *
 *  Solo obtiene token y recibe notificaciones.
 *  Toda la lógica de envío está en el backend .NET
 *
 * ---------------------------------------------------------------------------- */

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// ✅ Configuración pública de Firebase (restringir en Firebase Console)
const firebaseConfig = {
	apiKey: "AIzaSyCm5XluMK0YrNIX051eHUjJjGYKE40tCTA",
	authDomain: "montessori-nova-school.firebaseapp.com",
	projectId: "montessori-nova-school",
	storageBucket: "montessori-nova-school.firebasestorage.app",
	messagingSenderId: "219654717926",
	appId: "1:219654717926:web:0f8a4a94fd7afe2b739627",
	measurementId: "G-EL4RF1LJ07"
};

// ✅ VAPID Key (obtener de Firebase Console → Cloud Messaging)
const VAPID_KEY = "BHWr9q3WM11DAOWlOCcVeGDgOkaXZ0deREB3TYcGTvhbFCoz2IyGOHMMi7BRBC29a_mNcaCWx9v8J3BalPLgoD4";

// Initialize Firebase
let app = null;
let messaging = null;
let isInitialized = false;

/**
 * Inicializa Firebase de forma segura
 */
function initFirebase() {
	if (isInitialized) {
		console.log('🔥 Firebase ya inicializado');
		return messaging;
	}
	
	try {
		console.log('🔥 Inicializando Firebase...');
		
		// Verificar soporte de Service Worker y Push
		if (!('serviceWorker' in navigator)) {
			console.warn('⚠️ Service Workers no soportados');
			return null;
		}
		
		if (!('PushManager' in window)) {
			console.warn('⚠️ Push Notifications no soportadas');
			return null;
		}
		
		// Inicializar Firebase App
		app = initializeApp(firebaseConfig);
		console.log('✅ Firebase App inicializada');
		
		// Inicializar Firebase Messaging
		messaging = getMessaging(app);
		console.log('✅ Firebase Messaging inicializado');
		
		isInitialized = true;
		return messaging;
		
	} catch (error) {
		console.error('❌ Error inicializando Firebase:', error);
		return null;
	}
}

/**
 * Obtiene el token FCM y lo envía al backend para guardarlo
 * Este es el ÚNICO método que necesitas llamar desde Blazor al iniciar sesión
 */
export async function initializePushNotifications(dotNetRef) {
	console.log('📱 Iniciando notificaciones push...');
	
	// Inicializar Firebase si no está inicializado
	const messagingInstance = initFirebase();
	
	if (!messagingInstance) {
		console.warn('⚠️ Push notifications no disponibles');
		return false;
	}

	try {
		// 1. Verificar si ya hay permiso
		const currentPermission = Notification.permission;
		console.log('🔐 Permiso actual:', currentPermission);
		
		if (currentPermission === 'denied') {
			console.warn('⚠️ Usuario denegó notificaciones previamente');
			return false;
		}
		
		// 2. Solicitar permiso si es necesario
		let permission = currentPermission;
		if (permission === 'default') {
			console.log('🔔 Solicitando permiso de notificaciones...');
			permission = await Notification.requestPermission();
			console.log('🔐 Permiso obtenido:', permission);
		}
		
		if (permission !== 'granted') {
			console.log('⚠️ Notificaciones no autorizadas');
			return false;
		}

		// 3. Esperar a que el Service Worker esté activo
		const registration = await navigator.serviceWorker.ready;
		console.log('✅ Service Worker listo:', registration.scope);

		// 4. Obtener token FCM
		console.log('🔑 Obteniendo token FCM...');
		const token = await getToken(messagingInstance, { 
			vapidKey: VAPID_KEY,
			serviceWorkerRegistration: registration
		});
		
		if (!token) {
			console.error('❌ No se pudo obtener el token FCM');
			return false;
		}

		console.log('✅ Token FCM obtenido:', token.substring(0, 20) + '...');

		// 5. Enviar token al backend
		if (dotNetRef) {
			console.log('📤 Enviando token al backend...');
			await dotNetRef.invokeMethodAsync('OnFCMTokenReceived', token);
			console.log('✅ Token enviado al backend');
			
		}

		// 6. Configurar listener de notificaciones en primer plano
		setupForegroundListener(dotNetRef);
		console.log('✅ Listener de notificaciones configurado');

		// 7. ✅ NUEVO: Escuchar cambios/renovaciones de token
		setupTokenRefreshListener(dotNetRef);
		console.log('✅ Listener de renovación de token configurado');

		return true;

	} catch (error) {
		console.error('❌ Error inicializando notificaciones push:', error);
		console.error('Stack:', error.stack);
		return false;
	}
}

/**
 * Maneja notificaciones cuando la app está abierta
 */
function setupForegroundListener(dotNetRef) {
	if (!messaging) {
		console.warn('⚠️ Messaging no disponible para listener');
		return;
	}

	try {
		onMessage(messaging, (payload) => {
			console.log('🔔 Notificación recibida (app abierta):', payload);

			// Enviar a Blazor
			if (dotNetRef) {
				dotNetRef.invokeMethodAsync('OnNotificationReceived', {
					title: payload.notification?.title || '',
					body: payload.notification?.body || '',
					data: payload.data || {}
				}).catch(err => {
					console.error('Error invocando callback .NET:', err);
				});
			}

			// Mostrar notificación nativa del navegador
			if (Notification.permission === 'granted' && payload.notification) {
				new Notification(payload.notification.title || 'Notificación', {
					body: payload.notification.body || '',
					icon: '/_content/montessoriNova.Component/img/favicon/android-icon-192x192.png',
					badge: '/_content/montessoriNova.Component/img/favicon/favicon-96x96.png',
					tag: payload.data?.notificationId || 'default',
					requireInteraction: false
				});
			}
		});
		
		console.log('✅ Foreground message listener configurado');
		
	} catch (error) {
		console.error('❌ Error configurando foreground listener:', error);
	}
}

/**
 * ✅ NUEVO: Escucha cambios y renovaciones de token FCM
 * Firebase puede rotar tokens por seguridad, esta función detecta ese cambio
 * y actualiza automáticamente en el backend
 */
function setupTokenRefreshListener(dotNetRef) {
	if (!messaging) {
		console.warn('⚠️ Messaging no disponible para token refresh listener');
		return;
	}

	try {
		// Importar onTokenRefresh dinámicamente
		import('firebase/messaging').then(({ onMessage }) => {
			// Firebase SDK v9+ no tiene onTokenRefresh explícito
			// Pero getToken se puede llamar periódicamente o cuando detecte cambios
			
			// Verificar cambios de token cada hora
			setInterval(async () => {
				try {
					console.log('🔄 Verificando si el token FCM ha cambiado...');
					
					const registration = await navigator.serviceWorker.ready;
					const newToken = await getToken(messaging, {
						vapidKey: VAPID_KEY,
						serviceWorkerRegistration: registration
					});
					
					if (newToken) {
						console.log('🔄 Token FCM renovado, actualizando en backend...');
						
						if (dotNetRef) {
							await dotNetRef.invokeMethodAsync('OnFCMTokenReceived', newToken);
							console.log('✅ Token renovado guardado en backend');
						}
					}
				} catch (error) {
					console.error('❌ Error verificando token refresh:', error);
				}
			}, 60 * 60 * 1000); // Cada hora
			
			console.log('✅ Token refresh check configurado (cada hora)');
		});
	} catch (error) {
		console.error('❌ Error configurando token refresh listener:', error);
	}
}

/**
 * Verifica si las notificaciones están soportadas
 */
export function isNotificationSupported() {
	const supported = 'serviceWorker' in navigator && 
					  'PushManager' in window && 
					  'Notification' in window;
	console.log('🔍 Notificaciones soportadas:', supported);
	return supported;
}

/**
 * Obtiene el estado del permiso de notificaciones
 */
export function getNotificationPermission() {
	if (!('Notification' in window)) {
		console.log('🔍 Permiso notificaciones: unsupported');
		return 'unsupported';
	}
	const permission = Notification.permission;
	console.log('🔍 Permiso notificaciones:', permission);
	return permission;
}
